import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const LocationDropdown = ({
  options,
  selectedOption,
  setSelectedOption,
  placeholder,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const normalizedOptions = useMemo(() => {
    if (!options) return [];
    return options.map((opt) => ({
      value: opt.value || opt.name || "",
      label: opt.label || opt.name || "",
      ...opt,
    }));
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (query === "") return normalizedOptions;
    return normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    );
  }, [normalizedOptions, query]);

  const handleSelection = (option) => {
    // Toggle: if clicking the already selected option, deselect it
    if (selectedOption?.value === option.value) {
      setSelectedOption(null);
      setQuery("");
    } else {
      setSelectedOption(option);
      setQuery("");
    }
    setIsOpen(false);
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Only reset query if there's no selection or if it doesn't match
        setQuery((currentQuery) => {
          if (!selectedOption || currentQuery !== selectedOption.label) {
            return "";
          }
          return currentQuery;
        });
      }
    },
    [selectedOption]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center min-h-[24px]">
          {!selectedOption && !query && (
            <MagnifyingGlassIcon className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
          )}
          <input
            ref={searchRef}
            type="text"
            className={`flex-1 min-w-0 border-0 bg-transparent p-0 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6 cursor-pointer ${
              !selectedOption && !query ? "pl-5" : ""
            }`}
            placeholder={selectedOption ? "" : placeholder}
            value={
              query !== "" ? query : selectedOption ? selectedOption.label : ""
            }
            onChange={(e) => {
              const newQuery = e.target.value;
              setQuery(newQuery);
              // If user is typing and there's a selection, clear it
              if (selectedOption && newQuery !== selectedOption.label) {
                setSelectedOption(null);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
          />
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-none sm:text-sm">
          <ul className="max-h-60 py-1">
            {filteredOptions.map((option) => {
              const isSelected = selectedOption?.value === option.value;
              return (
                <li
                  key={option.value}
                  className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    isSelected ? "bg-indigo-50" : "hover:bg-indigo-50"
                  }`}
                  onClick={() => handleSelection(option)}
                >
                  <span
                    className={`block truncate ${
                      isSelected ? "font-semibold" : "font-normal"
                    }`}
                  >
                    {option.label}
                  </span>
                  {isSelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  )}
                </li>
              );
            })}
            {filteredOptions.length === 0 && (
              <li className="py-2 px-4 text-sm text-gray-500">
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
